/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import Styles from './components/InputText/styles.module.sass'
import { useBreakpointValue } from '@chakra-ui/react'

const FILTER_REQUIRED_ERROR = 'You must select a filter'
const VALUE_REQUIRED_ERROR = 'You must write a value'

export const useDefaultFilterInput = ({
  initialFilterId = '',
  initialFilterValue = '',
  filterOptions,
  onFilter,
  onReset
}) => {
  const [currentFilterState, setCurrentFilterState] = useState({
    id: initialFilterId,
    value: initialFilterValue,
    type: 'text',
    error: null
  })
  const filterTypeMap = useMemo(() => {
    return filterOptions.reduce((map, filter) => {
      map[filter.id] = filter.type || 'text'
      return map
    }, {})
  }, [filterOptions])

  const validateFilter = useCallback((id, value, type) => {
    if (!id?.trim()) {
      return { isValid: false, message: FILTER_REQUIRED_ERROR }
    }

    const normalizedValue = typeof value === 'string' ? value.trim() : value
    if (normalizedValue === '' || normalizedValue == null) {
      return { isValid: false, message: VALUE_REQUIRED_ERROR }
    }

    return { isValid: true, message: null }
  }, [])

  const formattedFilters = useMemo(() => {
    return filterOptions.map((filter) => {
      const { id, label, type = 'text' } = filter

      const action = () => {
        filter.action?.()

        setCurrentFilterState(
          prevState => {
            return ({
              ...prevState,
              id,
              type,
              value: '',
              error: null
            })
          }
        )
      }

      return { id, label, action }
    })
  }, [filterOptions])

  const updateFilterValue = useCallback((value) => {
    setCurrentFilterState(
      prevState => {
        return ({
          ...prevState,
          value,
          error: null
        })
      }
    )
  }, [])

  const selectFilter = useCallback((filterId) => {
    const newType = filterTypeMap[filterId] || 'text'
    setCurrentFilterState(prevState => ({
      ...prevState,
      id: filterId,
      type: newType,
      error: null
    }))
  }, [filterTypeMap])

  const applyFilter = useCallback(() => {
    const { id, value, type } = currentFilterState
    const validation = validateFilter(id, value, type)

    if (!validation.isValid) {
      setCurrentFilterState(prevState => ({
        ...prevState,
        error: validation.message
      }))
      return false
    }

    onFilter?.(id, value.toString().trim())
    return true
  }, [currentFilterState, validateFilter, onFilter])

  const resetFilter = useCallback(() => {
    setCurrentFilterState({
      id: '',
      value: '',
      type: 'text',
      error: null
    })
    onReset?.()
  }, [onReset])

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      applyFilter()
    }
  }, [applyFilter])

  const handleInputChange = (e) => {
    updateFilterValue(e.target.value)
  }

  const handleAutocompleteChange = (option) => {
    updateFilterValue(option?.value || '')
  }

  const filterContainerClass = useBreakpointValue({
    sm: '',
    md: Styles.inputBox
  })

  useEffect(() => {
    setCurrentFilterState(prevState => ({
      ...prevState,
      id: initialFilterId,
      value: initialFilterValue,
      type: filterTypeMap[initialFilterId] || 'text'
    }))
  }, [initialFilterId, initialFilterValue, filterTypeMap])

  return {
    applyFilter,
    availableFilters: formattedFilters,
    errorMessage: currentFilterState.error,
    filterContainerClass,
    filterId: currentFilterState.id,
    filterSelected: currentFilterState.id,
    filterType: currentFilterState.type,
    filterValue: currentFilterState.value,
    handleAutocompleteChange,
    handleInputChange,
    handleKeyPress,
    hasError: Boolean(currentFilterState.error),
    isFilterSelected: Boolean(currentFilterState.id),
    resetFilter,
    selectFilter,
    updateFilterValue
  }
}
